import * as THREE from 'three';
import { colored } from '../../Style.js';

// Sci-fi art direction: dark brushed alloy, crisp panel lines and clean
// cyan/violet light under a night sky. Glows are unlit and skip tone mapping
// so they stay vivid without a bloom pass.
export const SCIFI = {
  alloy: 0x3a4350,
  alloyLight: 0x5d6978,
  alloyDark: 0x20262e,
  alloyDeep: 0x14181e,
  panel: 0x2e3540,
  trim: 0xc9d4e0,
  cyan: 0x38e1ff,
  cyanSoft: 0x2a9fd6,
  violet: 0x9b6bff,
  amber: 0xffb02e,
  hazard: 0xf2b705,
  hazardDark: 0x1c1d22,
  warning: 0xff4a3d,
  heal: 0x3dffc0,
  neutral: 0xd6e2ee,
  containers: [0x2f6f8f, 0x8f4a2f, 0x5b6470, 0x3f7a5a, 0x7a6a3a, 0x48466e]
};

const metalCache = new Map();

// Lit panel material for merged, vertex-coloured hard-surface parts.
export function metal(color = 0xffffff, opts = {}) {
  const key = `${color}|${opts.roughness ?? 0.5}|${opts.metalness ?? 0.45}|${opts.vertexColors ? 1 : 0}|${opts.flat ? 1 : 0}`;
  if (!opts.unique && metalCache.has(key)) return metalCache.get(key);
  const material = new THREE.MeshStandardMaterial({
    color,
    roughness: opts.roughness ?? 0.5,
    metalness: opts.metalness ?? 0.45,
    flatShading: !!opts.flat,
    vertexColors: !!opts.vertexColors
  });
  if (!opts.unique) metalCache.set(key, material);
  return material;
}

const glowCache = new Map();

// Unlit glow. `boost` > 1 pushes the colour past white-point for a hot core.
export function glow(color, { opacity = 1, additive = false, boost = 1, vertexColors = false, unique = false, side = THREE.FrontSide } = {}) {
  const key = `${color}|${opacity}|${additive ? 1 : 0}|${boost}|${vertexColors ? 1 : 0}|${side}`;
  if (!unique && glowCache.has(key)) return glowCache.get(key);
  const material = new THREE.MeshBasicMaterial({
    color: new THREE.Color(color).multiplyScalar(boost),
    transparent: additive || opacity < 1,
    opacity,
    blending: additive ? THREE.AdditiveBlending : THREE.NormalBlending,
    depthWrite: !additive && opacity >= 1,
    vertexColors,
    side,
    toneMapped: false,
    fog: !additive
  });
  if (!unique) glowCache.set(key, material);
  return material;
}

let glowTexture = null;

// Soft round sprite used for halos, lights and particles.
export function getGlowTexture() {
  if (glowTexture) return glowTexture;
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = 64;
  const ctx = canvas.getContext('2d');
  const gradient = ctx.createRadialGradient(32, 32, 0, 32, 32, 32);
  gradient.addColorStop(0, 'rgba(255,255,255,1)');
  gradient.addColorStop(0.2, 'rgba(255,255,255,0.75)');
  gradient.addColorStop(0.5, 'rgba(255,255,255,0.22)');
  gradient.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, 64, 64);
  glowTexture = new THREE.CanvasTexture(canvas);
  glowTexture.colorSpace = THREE.SRGBColorSpace;
  return glowTexture;
}

// An additive halo sprite that always faces the camera.
export function halo(color, size, opacity = 1) {
  const sprite = new THREE.Sprite(new THREE.SpriteMaterial({
    map: getGlowTexture(),
    color,
    transparent: true,
    opacity,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
    toneMapped: false
  }));
  sprite.scale.setScalar(size);
  return sprite;
}

// Mix a team colour towards white for a hot core that still reads as the team.
export function hot(color, amount = 0.55) {
  return new THREE.Color(color).lerp(new THREE.Color(0xffffff), amount);
}

// Stable hash of a string id, for seeding per-object variation.
export function hashId(id) {
  let h = 2166136261;
  const str = String(id);
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

// Black/yellow hazard stripes: a row of vertex-coloured blocks along X,
// then turned by ry and moved into place.
export function hazardStripes(length, width, height, { x = 0, y = 0, z = 0, ry = 0, stripe = 0.5 } = {}) {
  const parts = [];
  const count = Math.max(2, Math.round(length / stripe));
  const step = length / count;
  for (let i = 0; i < count; i++) {
    const geo = colored(new THREE.BoxGeometry(step, height, width), i % 2 ? SCIFI.hazardDark : SCIFI.hazard, {
      x: -length / 2 + step * (i + 0.5)
    });
    geo.rotateY(ry);
    geo.translate(x, y, z);
    parts.push(geo);
  }
  return parts;
}
